import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { V2_SUPPLIERS_PATH } from '@/v2/utils/routes'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import {
  useClearProductCatalogSupplier,
  useListCatalogSuppliers,
  useSetProductCatalogSupplier,
  type CatalogSupplier,
  type ProductCatalogSupplierSummary,
} from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'

type V2ProductCatalogSupplierFormValues = {
  catalogSupplierId: string
}

type V2ProductCatalogSupplierDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  currentSupplier: ProductCatalogSupplierSummary | null
}

export function V2ProductCatalogSupplierDialog({
  open,
  onOpenChange,
  productId,
  currentSupplier,
}: V2ProductCatalogSupplierDialogProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: setSupplier, isPending: isSettingSupplier } =
    useSetProductCatalogSupplier(productId)
  const { mutateAsync: clearSupplier, isPending: isClearingSupplier } =
    useClearProductCatalogSupplier(productId)
  const { data: suppliers = [], isLoading: isLoadingSuppliers } =
    useListCatalogSuppliers()

  const form = useForm<V2ProductCatalogSupplierFormValues>({
    defaultValues: {
      catalogSupplierId: currentSupplier?.id ?? '',
    },
  })

  const isPending = isSettingSupplier || isClearingSupplier

  useEffect(() => {
    if (open) {
      form.reset({
        catalogSupplierId: currentSupplier?.id ?? '',
      })
    }
  }, [open, currentSupplier, form])

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset({
        catalogSupplierId: currentSupplier?.id ?? '',
      })
    }
    onOpenChange(nextOpen)
  }

  function validateSupplierId(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t(
        'v2.pages.products.detail.productSupplier.dialog.validation.supplierRequired'
      )
    }
    return true
  }

  async function handleSubmit(values: V2ProductCatalogSupplierFormValues) {
    try {
      await setSupplier({ catalogSupplierId: values.catalogSupplierId })
      onOpenChange(false)
    } catch {
      toast.error(
        t('v2.pages.products.detail.productSupplier.dialog.errors.submitFailed')
      )
    }
  }

  async function handleClear() {
    try {
      await clearSupplier()
      onOpenChange(false)
    } catch {
      toast.error(
        t('v2.pages.products.detail.productSupplier.dialog.errors.clearFailed')
      )
    }
  }

  const showNoSuppliers = !isLoadingSuppliers && suppliers.length === 0
  const showClearButton = currentSupplier != null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('v2.pages.products.detail.productSupplier.dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('v2.pages.products.detail.productSupplier.dialog.description')}
          </DialogDescription>
        </DialogHeader>
        {showNoSuppliers ? (
          <p className='text-sm text-muted-foreground'>
            {t('v2.pages.products.detail.productSupplier.dialog.noSuppliers')}{' '}
            <Link
              className='font-medium text-primary underline-offset-4 hover:underline'
              to={V2_SUPPLIERS_PATH}
            >
              {t(
                'v2.pages.products.detail.productSupplier.dialog.addSuppliersLink'
              )}
            </Link>
          </p>
        ) : (
          <Form {...form}>
            <form
              className='space-y-4'
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name='catalogSupplierId'
                rules={{ validate: validateSupplierId }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(
                        'v2.pages.products.detail.productSupplier.dialog.supplierLabel'
                      )}
                    </FormLabel>
                    <FormControl>
                      <V2ProductCatalogSupplierCombobox
                        disabled={isPending || isLoadingSuppliers}
                        emptyText={t(
                          'v2.pages.products.detail.productSupplier.dialog.supplierEmpty'
                        )}
                        onValueChange={field.onChange}
                        placeholder={t(
                          'v2.pages.products.detail.productSupplier.dialog.supplierSearchPlaceholder'
                        )}
                        suppliers={suppliers}
                        value={field.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className='gap-2 sm:justify-between'>
                {showClearButton ? (
                  <Button
                    disabled={isPending}
                    onClick={() => {
                      void handleClear()
                    }}
                    type='button'
                    variant='outline'
                  >
                    {isClearingSupplier ? (
                      <Loader2 className='size-4 animate-spin' aria-hidden />
                    ) : null}
                    {t(
                      'v2.pages.products.detail.productSupplier.dialog.clearButton'
                    )}
                  </Button>
                ) : (
                  <span />
                )}
                <div className='flex gap-2'>
                  <Button
                    disabled={isPending}
                    onClick={() => {
                      handleOpenChange(false)
                    }}
                    type='button'
                    variant='outline'
                  >
                    {t(
                      'v2.pages.products.detail.productSupplier.dialog.cancelButton'
                    )}
                  </Button>
                  <Button disabled={isPending} type='submit'>
                    {isSettingSupplier ? (
                      <Loader2 className='size-4 animate-spin' aria-hidden />
                    ) : null}
                    {t(
                      'v2.pages.products.detail.productSupplier.dialog.submitButton'
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}

type V2ProductCatalogSupplierComboboxProps = {
  suppliers: CatalogSupplier[]
  value: string
  onValueChange: (supplierId: string) => void
  placeholder: string
  emptyText: string
  disabled?: boolean
}

function V2ProductCatalogSupplierCombobox({
  suppliers,
  value,
  onValueChange,
  placeholder,
  emptyText,
  disabled = false,
}: V2ProductCatalogSupplierComboboxProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const supplierIds = useMemo(
    () => suppliers.map((supplier) => supplier.id),
    [suppliers]
  )

  const supplierNameById = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.id, supplier.name]))
  }, [suppliers])

  const filteredSupplierIds = useMemo(() => {
    const query = inputValue.trim().toLowerCase()
    if (query.length === 0) {
      return supplierIds
    }
    return supplierIds.filter((supplierId) => {
      const name = supplierNameById.get(supplierId) ?? ''
      return name.toLowerCase().includes(query)
    })
  }, [inputValue, supplierIds, supplierNameById])

  const hasSelectedSupplier = value.trim().length > 0

  useEffect(() => {
    if (value.trim().length > 0) {
      setInputValue(supplierNameById.get(value) ?? '')
    }
  }, [value, supplierNameById])

  function syncInputToSelection() {
    if (value.trim().length > 0) {
      setInputValue(supplierNameById.get(value) ?? '')
      return
    }
    setInputValue('')
  }

  return (
    <Combobox
      disabled={disabled}
      items={supplierIds}
      filteredItems={filteredSupplierIds}
      filter={null}
      autoHighlight={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) {
          syncInputToSelection()
        }
      }}
      inputValue={inputValue}
      onInputValueChange={(next) => {
        const nextInput = next ?? ''
        setInputValue(nextInput)
        if (!hasSelectedSupplier) {
          return
        }
        const selectedName = supplierNameById.get(value) ?? ''
        if (nextInput.trim() !== selectedName) {
          onValueChange('')
        }
      }}
      value={hasSelectedSupplier ? value : null}
      onValueChange={(supplierId) => {
        if (supplierId == null) {
          onValueChange('')
          setInputValue('')
          return
        }
        onValueChange(supplierId)
        setInputValue(supplierNameById.get(supplierId) ?? '')
        setOpen(false)
      }}
      isItemEqualToValue={(left, right) => left === right}
      itemToStringLabel={(supplierId) => supplierNameById.get(supplierId) ?? ''}
    >
      <ComboboxInput
        autoComplete='off'
        className='w-full'
        disabled={disabled}
        placeholder={placeholder}
        showClear={hasSelectedSupplier}
        showTrigger={false}
      />
      <ComboboxContent className='w-(--anchor-width) min-w-55 p-0'>
        <ComboboxList>
          <ComboboxEmpty>{emptyText}</ComboboxEmpty>
          {filteredSupplierIds.map((supplierId) => (
            <ComboboxItem key={supplierId} value={supplierId}>
              {supplierNameById.get(supplierId)}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
